import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type Patient } from '../../services/OpdPatientService'
import { APP_FLOW_NAMES } from '../../utils/Constants'

interface PatientListState {
  patients: Patient[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  loading: boolean
  selectedPatient: Patient | null
  search: {
    query: string
    param: string
  }
  filter: {
    opdTerm: string
    ipdTerm: string
  }
}

const initialState: PatientListState = {
  patients: [],
  page: 0,
  pageSize: 30,
  totalCount: 0,
  totalPages: 0,
  loading: false,
  selectedPatient: null,
  search: {
    query: '',
    param: '',
  },
  filter: {
    opdTerm: 'ALL',
    ipdTerm: 'ALL',
  },
}

const PatientListSlice = createSlice({
  name: 'patientList',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Set patients list
    setPatients: (state, action: PayloadAction<Patient[]>) => {
      state.patients = action.payload
    },

    // Set pagination data
    setPaginationData: (
      state,
      action: PayloadAction<{
        page?: number
        pageSize?: number
        totalCount?: number
        totalPages?: number
      }>
    ) => {
      if (action.payload.page !== undefined) {
        state.page = action.payload.page
      }
      if (action.payload.pageSize !== undefined) {
        state.pageSize = action.payload.pageSize
      }
      if (action.payload.totalCount !== undefined) {
        state.totalCount = action.payload.totalCount
      }
      if (action.payload.totalPages !== undefined) {
        state.totalPages = action.payload.totalPages
      }
    },

    // Set page
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },

    // Set page size
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
      state.page = 0 // Reset to first page when changing page size
    },

    // Set total count
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload
    },

    // Set total pages
    setTotalPages: (state, action: PayloadAction<number>) => {
      state.totalPages = action.payload
    },

    // Set search query
    setSearch: (
      state,
      action: PayloadAction<{ query: string; param: string }>
    ) => {
      state.search = action.payload
      state.page = 0 // Reset to first page when searching
    },

    // Clear search
    clearSearch: (state) => {
      state.search = {
        query: '',
        param: '',
      }
      state.page = 0 // Reset to first page when clearing search
    },

    // Set filter term for a specific flow
    setFilter: (
      state,
      action: PayloadAction<{ flow: 'opd' | 'ipd'; term: string }>
    ) => {
      if (action.payload.flow === APP_FLOW_NAMES.OPD) {
        state.filter.opdTerm = action.payload.term
      } else if (action.payload.flow === APP_FLOW_NAMES.IPD) {
        state.filter.ipdTerm = action.payload.term
      }
      state.page = 0 // Reset to first page when applying filter
    },

    // Clear filter
    clearFilter: (state) => {
      state.filter = {
        opdTerm: 'ALL',
        ipdTerm: 'ALL',
      }
      state.page = 0 // Reset to first page when clearing filter
    },

    // Set selected patient
    setSelectedPatient: (state, action: PayloadAction<Patient | null>) => {
      state.selectedPatient = action.payload
    },

    // Clear selected patient
    clearSelectedPatient: (state) => {
      state.selectedPatient = null
    },

    // Set complete patient list data (for API responses)
    setPatientListData: (
      state,
      action: PayloadAction<{
        patients: Patient[]
        page: number
        pageSize: number
        totalCount: number
        totalPages?: number
        selectedPatient?: Patient | null
      }>
    ) => {
      state.patients = action.payload.patients
      state.page = action.payload.page
      state.pageSize = action.payload.pageSize
      state.totalCount = action.payload.totalCount
      if (action.payload.totalPages !== undefined) {
        state.totalPages = action.payload.totalPages
      }
      if (action.payload.selectedPatient !== undefined) {
        state.selectedPatient = action.payload.selectedPatient
      }
    },

    // Add a single patient to the list
    addPatient: (state, action: PayloadAction<Patient>) => {
      state.patients.push(action.payload)
      state.totalCount += 1
    },

    // Update a patient in the list
    updatePatientInList: (
      state,
      action: PayloadAction<{ patientId: string; updates: Partial<Patient> }>
    ) => {
      const index = state.patients.findIndex(
        (patient) => patient.patientId === action.payload.patientId
      )
      if (index !== -1) {
        state.patients[index] = {
          ...state.patients[index],
          ...action.payload.updates,
        }
      }
      // Also update selected patient if it's the same one
      if (state.selectedPatient?.patientId === action.payload.patientId) {
        state.selectedPatient = {
          ...state.selectedPatient,
          ...action.payload.updates,
        }
      }
    },

    // Remove a patient from the list
    removePatientFromList: (state, action: PayloadAction<string>) => {
      state.patients = state.patients.filter(
        (patient) => patient.patientId !== action.payload
      )
      state.totalCount = Math.max(0, state.totalCount - 1)
      // Clear selected patient if it was the removed one
      if (state.selectedPatient?.patientId === action.payload) {
        state.selectedPatient = null
      }
    },

    // Clear all patient list data
    clearPatientList: (state) => {
      state.patients = []
      state.page = 0
      state.pageSize = 30
      state.totalCount = 0
      state.totalPages = 0
      state.selectedPatient = null
      state.search = {
        query: '',
        param: '',
      }
      state.loading = false
    },

    // Reset pagination to defaults
    resetPagination: (state) => {
      state.page = 0
      state.pageSize = 30
    },
  },
})

export const {
  setLoading,
  setPatients,
  setPaginationData,
  setPage,
  setPageSize,
  setTotalCount,
  setTotalPages,
  setSearch,
  clearSearch,
  setFilter,
  clearFilter,
  setSelectedPatient,
  clearSelectedPatient,
  setPatientListData,
  addPatient,
  updatePatientInList,
  removePatientFromList,
  clearPatientList,
  resetPagination,
} = PatientListSlice.actions

// Selectors for easy access to state values
export const selectPatientList = (state: { patientList: PatientListState }) =>
  state.patientList
export const selectPatients = (state: { patientList: PatientListState }) =>
  state.patientList.patients
export const selectPagination = (state: { patientList: PatientListState }) => ({
  page: state.patientList.page,
  pageSize: state.patientList.pageSize,
  totalCount: state.patientList.totalCount,
  totalPages: state.patientList.totalPages,
})
export const selectSelectedPatient = (state: {
  patientList: PatientListState
}) => state.patientList.selectedPatient
export const selectLoading = (state: { patientList: PatientListState }) =>
  state.patientList.loading
export const selectSearch = (state: { patientList: PatientListState }) =>
  state.patientList.search
export const selectFilter = (state: { patientList: PatientListState }) =>
  state.patientList.filter

export default PatientListSlice.reducer
